import { createSlice } from '@reduxjs/toolkit';

// Load draft from localStorage if available
const loadDraftFromStorage = () => {
  try {
    const savedDraft = localStorage.getItem('associationDraftFormData');
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch (error) {
    console.error('Error loading association draft from localStorage:', error);
    return null;
  }
};

const initialState = {
  associations: [],
  currentAssociation: null,
  isLoading: false,
  members: [],
  isMembersLoading: false,
  membersError: null,
  draftFormData: loadDraftFromStorage(), // Load draft from localStorage on init
  filters: {
    search: '',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const associationSlice = createSlice({
  name: 'association',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAssociation: (state) => {
      state.currentAssociation = null;
      state.members = [];
      state.membersError = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
    // Save draft form data
    saveAssociationDraft: (state, action) => {
      state.draftFormData = action.payload;
      try {
        localStorage.setItem('associationDraftFormData', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving association draft to localStorage:', error);
      }
    },
    // Clear draft form data
    clearAssociationDraft: (state) => {
      state.draftFormData = null;
      try {
        localStorage.removeItem('associationDraftFormData');
      } catch (error) {
        console.error('Error clearing association draft from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Associations
      .addCase('association/getAssociations/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/getAssociations/fulfilled', (state, action) => {
        state.isLoading = false;

        if (Array.isArray(action.payload)) {
          state.associations = action.payload;
        } else if (action.payload?.data) {
          state.associations = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.associations = Array.isArray(action.payload) ? action.payload : [];
        }

        state.error = null;
      })
      .addCase('association/getAssociations/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch associations';
      })
      // Fetch Associations with Filters
      .addCase('association/fetchAssociationsWithFilters/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/fetchAssociationsWithFilters/fulfilled', (state, action) => {
        state.isLoading = false;

        if (Array.isArray(action.payload)) {
          state.associations = action.payload;
        } else if (action.payload?.data) {
          state.associations = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.associations = Array.isArray(action.payload) ? action.payload : [];
        }

        state.error = null;
      })
      .addCase('association/fetchAssociationsWithFilters/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch associations';
      })
      // Get Association by ID
      .addCase('association/getAssociationById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/getAssociationById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentAssociation = action.payload;
        state.error = null;
      })
      .addCase('association/getAssociationById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch association details';
      })
      // Create Association
      .addCase('association/createAssociation/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/createAssociation/fulfilled', (state, action) => {
        state.isLoading = false;
        state.associations.unshift(action.payload); // Add new association to the beginning
        state.error = null;
      })
      .addCase('association/createAssociation/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create association';
      })
      // Update Association
      .addCase('association/updateAssociation/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/updateAssociation/fulfilled', (state, action) => {
        state.isLoading = false;
        const updatedAssociation = action.payload?.data || action.payload;
        const index = state.associations.findIndex((item) => {
          const itemId = item._id || item.id;
          const updatedId = updatedAssociation._id || updatedAssociation.id;
          return itemId && itemId === updatedId;
        });
        if (index !== -1) {
          state.associations[index] = updatedAssociation;
        }
        if (
          state.currentAssociation &&
          (state.currentAssociation._id === updatedAssociation._id || state.currentAssociation.id === updatedAssociation.id)
        ) {
          state.currentAssociation = updatedAssociation;
        }
        state.error = null;
      })
      .addCase('association/updateAssociation/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update association';
      })
      // Deactivate Association
      .addCase('association/deactivateAssociation/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/deactivateAssociation/fulfilled', (state, action) => {
        state.isLoading = false;
        // Update in list
        const index = state.associations.findIndex(
          (item) => item._id === action.payload || item.id === action.payload
        );
        if (index !== -1) {
          state.associations[index].isActive = false;
        }
        // Update current association
        if (
          state.currentAssociation &&
          (state.currentAssociation._id === action.payload || state.currentAssociation.id === action.payload)
        ) {
          state.currentAssociation.isActive = false;
        }
        state.error = null;
      })
      .addCase('association/deactivateAssociation/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to deactivate association';
      })
      // Get Association Members
      .addCase('association/getMembers/pending', (state) => {
        state.isMembersLoading = true;
        state.membersError = null;
      })
      .addCase('association/getMembers/fulfilled', (state, action) => {
        state.isMembersLoading = false;
        state.members = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
        state.membersError = null;
      })
      .addCase('association/getMembers/rejected', (state, action) => {
        state.isMembersLoading = false;
        state.membersError = action.payload || 'Failed to fetch association members';
      })
      // Send Onboarding Email
      .addCase('association/sendOnboardingEmail/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('association/sendOnboardingEmail/fulfilled', (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase('association/sendOnboardingEmail/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to send onboarding email';
      });
  },
});

export const {
  clearError,
  clearCurrentAssociation,
  setFilters,
  setPagination,
  setPage,
  setLimit,
  setCurrentPage,
  setItemsPerPage,
  resetPagination,
  saveAssociationDraft,
  clearAssociationDraft,
} = associationSlice.actions;

export default associationSlice.reducer;
